import { useQuery } from '@tanstack/react-query';
import type {
  CustomerUsersResponse,
  FailedNotificationsResponse,
  FailuresByReasonResponse,
  NotificationChannel,
  NotificationsByCustomerResponse,
  NotificationSummaryResponse,
  NotificationTrendResponse,
  UserNotificationsResponse,
} from '@metron/shared';
import { useApiClient } from './useApiClient.ts';

/** 'all' means no channel filter. */
export type ChannelFilter = NotificationChannel | 'all';

function baseParams(days: number, channel: ChannelFilter, customerId?: number | null) {
  const params = new URLSearchParams({ days: days.toString() });
  if (channel !== 'all') params.set('channel', channel);
  if (customerId) params.set('customerId', customerId.toString());
  return params;
}

const REFETCH = 5 * 60 * 1000;

export function useNotificationSummary(
  days: number,
  channel: ChannelFilter,
  customerId?: number | null,
) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['notification-overview', 'summary', days, channel, customerId],
    queryFn: () =>
      apiClient.get<NotificationSummaryResponse>(
        `notification-overview/summary?${baseParams(days, channel, customerId)}`,
      ),
    refetchInterval: REFETCH,
  });
}

export function useNotificationTrend(
  days: number,
  channel: ChannelFilter,
  interval: 'day' | 'hour',
  customerId?: number | null,
) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['notification-overview', 'trend', days, channel, interval, customerId],
    queryFn: () => {
      const params = baseParams(days, channel, customerId);
      params.set('interval', interval);
      return apiClient.get<NotificationTrendResponse>(
        `notification-overview/trend?${params}`,
      );
    },
    refetchInterval: REFETCH,
  });
}

export function useNotificationsByCustomer(days: number, channel: ChannelFilter) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['notification-overview', 'by-customer', days, channel],
    queryFn: () =>
      apiClient.get<NotificationsByCustomerResponse>(
        `notification-overview/by-customer?${baseParams(days, channel)}`,
      ),
    refetchInterval: REFETCH,
  });
}

export function useFailuresByReason(
  days: number,
  channel: ChannelFilter,
  customerId?: number | null,
) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['notification-overview', 'failures-by-reason', days, channel, customerId],
    queryFn: () =>
      apiClient.get<FailuresByReasonResponse>(
        `notification-overview/failures-by-reason?${baseParams(days, channel, customerId)}`,
      ),
    refetchInterval: REFETCH,
  });
}

// --- VIEW_USERS-gated drill-downs (pass enabled from the caller) ---

export function useNotificationsByRecipient(
  days: number,
  channel: ChannelFilter,
  enabled: boolean,
) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['notification-overview', 'by-recipient', days, channel],
    queryFn: () =>
      apiClient.get<CustomerUsersResponse>(
        `notification-overview/by-recipient?${baseParams(days, channel)}`,
      ),
    enabled,
    refetchInterval: REFETCH,
  });
}

export function useCustomerUsers(
  customerId: number | null,
  days: number,
  channel: ChannelFilter,
  enabled: boolean,
) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['notification-overview', 'customer-users', customerId, days, channel],
    queryFn: () =>
      apiClient.get<CustomerUsersResponse>(
        `notification-overview/customers/${customerId}/users?${baseParams(days, channel)}`,
      ),
    enabled: enabled && !!customerId,
  });
}

export function useUserNotifications(
  userAccountId: number | null,
  days: number,
  channel: ChannelFilter,
  enabled: boolean,
) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['notification-overview', 'user-notifications', userAccountId, days, channel],
    queryFn: () =>
      apiClient.get<UserNotificationsResponse>(
        `notification-overview/users/${userAccountId}/notifications?${baseParams(days, channel)}`,
      ),
    enabled: enabled && !!userAccountId,
  });
}

export function useNotificationFailures(
  days: number,
  channel: ChannelFilter,
  enabled: boolean,
  customerId?: number | null,
) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['notification-overview', 'failures', days, channel, customerId],
    queryFn: () =>
      apiClient.get<FailedNotificationsResponse>(
        `notification-overview/failures?${baseParams(days, channel, customerId)}`,
      ),
    enabled,
    refetchInterval: REFETCH,
  });
}
